import { useTranslation } from 'react-i18next';
import { useWatches } from '../hooks/useWatches';
import { useToggleWatch } from '../hooks/useToggleWatch';

interface Props {
  /** 상세 헤더 스트립에서 현재 보고 있는 노드의 코드(C0xx/G0xx) */
  moduleCode: string;
}

/**
 * WatchToggle — queue #677 §UAT-2026-07-13-R677 §R677-4① 상세 헤더 ★구독 토글.
 * §R679 CopyButton 과 같은 헤더 스트립에 나란히 배치된다(호출부 `CommonShowcasePage.tsx`
 * `copyLinkStrip` — 노드가 선택돼 있을 때만 그 스트립 자체가 렌더되므로 이 컴포넌트는
 * 항상 `moduleCode` 가 유효하다는 전제로 단순하게 구현한다).
 *
 * 클릭 → 낙관적 반전(`useToggleWatch` onMutate) → PUT/DELETE → 서버 확정 목록으로 정합.
 * 실패 시 rollback(자동) + 인라인 오류 문구 · `showcase.common.retry`(§R656 기존 키 재사용)로
 * 동일 뮤테이션 재시도.
 */
export function WatchToggle({ moduleCode }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useWatches();
  const toggle = useToggleWatch();

  const subscribed = !!data?.moduleCodes.includes(moduleCode);
  const disabled = isLoading || toggle.isPending;

  function handleToggle() {
    toggle.mutate({ moduleCode, nextSubscribed: !subscribed });
  }

  function handleRetry() {
    if (toggle.variables) toggle.mutate(toggle.variables);
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
      <button
        type="button"
        data-testid="showcase-watch-toggle"
        aria-pressed={subscribed}
        aria-label={t('showcase.watch.aria', { code: moduleCode })}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-1)',
          padding: 'var(--spacing-1) var(--spacing-3)',
          border: `1px solid ${subscribed ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
          borderRadius: 'var(--radius-md)',
          background: subscribed ? 'var(--color-brand-primary)' : 'transparent',
          color: subscribed ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          fontFamily: 'var(--font-family-sans)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span aria-hidden="true">{subscribed ? '★' : '☆'}</span>
        {subscribed ? t('showcase.watch.subscribed') : t('showcase.watch.subscribe')}
      </button>

      {toggle.isError && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
          <span role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}>
            {t('showcase.watch.error')}
          </span>
          <button
            type="button"
            onClick={handleRetry}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-brand-primary)',
              fontWeight: 600,
              textDecoration: 'underline',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('showcase.common.retry')}
          </button>
        </span>
      )}
    </div>
  );
}
